import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { StationMonitorStripView } from "./StationMonitorStrip";
import StationRowStatus, { STATUS_STICKY } from "./StationRowStatus";
import { useStationMonitor } from "../hooks/useStationMonitor";
import { worstStationException } from "../util/stationRowException";
import usePresence from "../hooks/usePresence";
import { buildPresenceRows, formatElapsedLabel, presenceKey, presenceWork } from "../util/scribePresence";

// A plain ticking clock (mirrors announcer-fe's OrderOfGo.jsx useNow), purely
// a re-render trigger so elapsed-time cells (and the quiet-threshold
// crossover, AC6) advance on screen between network polls rather than
// freezing at whatever value was true when the last poll landed.
function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const CLOCK_TICK_MS = 5000;

// #2365 (scribe-presence workstream, card D) — "Is anyone scribing ring 3
// right now? The C sheet hasn't moved in ten minutes, is that scribe gone or
// just slow? We're about to start a class, are all the seats covered?" One
// real table (AC4), one row per (ring, seat) the current class actually
// stages (AC1/AC2), showing who is connected and a real elapsed time next to
// it (AC3) rather than a bare verdict — colour is layered ON TOP of that
// number (a red Alert chip past SCRIBE_QUIET_THRESHOLD_MS, AC6), never
// instead of it. A seat with no row in GET /presence (nobody seen inside
// its one-hour window) reads as a plain, non-alarming "Not connected" (AC2).
// A seat that went quiet comes back as a stale row (live:false, #2370), so
// the quiet alarm is derived from the API response alone and survives a page
// reload; this dashboard keeps no memory of earlier polls.
//
// showId/ring/classId/seat on a presence row are CLIENT claims, not
// authorization facts (#2363) — this table reads as "who says they are
// connected here", never as "the system confirms this scribe is assigned".
// #2365 — `ringState` is ShowDashboard's single useRingStates(showId) call,
// shared with RingStateSection so the classes/rides/ring-state fetch happens
// once per mount, not once per section (a second, independent instance here
// doubled that request set on every dashboard load).
export default function ScribePresenceSection({ showId, ringState }) {
  const { t } = useTranslation("scoreadmin");
  const { rows: ringRows, classes, ridesById, loading: ringLoading, error: ringError } = ringState;
  const { observedByKey, loading: presenceLoading, error: presenceError } = usePresence(showId);
  const now = useNow(CLOCK_TICK_MS);
  // #3647: the station monitor is read HERE (once) so a station's worst
  // exception can sit on its own table row. The rows with no table row to sit
  // on, and every other exception, go to the strip above the table unchanged.
  const monitor = useStationMonitor(showId);
  const exceptionByDevice = useMemo(() => {
    const byDevice = new Map();
    for (const station of monitor.visibleRows) {
      const exception = worstStationException(station, now);
      if (exception && station.deviceId) byDevice.set(station.deviceId, exception);
    }
    return byDevice;
  }, [monitor.visibleRows, now]);
  const classesById = useMemo(
    () => Object.fromEntries((classes || []).map((c) => [c.id, c])),
    [classes]
  );

  if (!showId) return null;

  const loading = ringLoading || presenceLoading;
  const error = ringError || presenceError;
  const rows = buildPresenceRows({ ringRows, observedByKey, now });

  // #3647: a station's exception goes on the table row its presence sits on
  // (joined by deviceId = the presence row's instanceId), and ONLY there: a
  // station placed on a row is left out of the strip so it is never reported
  // twice. A station that matches no row (not in a seat, dropped out of
  // presence, or the table is not showing) stays in the strip, never dropped:
  // an unseated station holding unsent sheets is the stranded-score case.
  const tableShown = !loading && rows.length > 0;
  const rowExceptions = new Map();
  if (tableShown) {
    for (const row of rows) {
      const exception = row.instanceId ? exceptionByDevice.get(row.instanceId) : undefined;
      if (exception) rowExceptions.set(presenceKey(row), { exception, instanceId: row.instanceId });
    }
  }
  const placedDeviceIds = new Set([...rowExceptions.values()].map((v) => v.instanceId));
  const stripRows = monitor.visibleRows.filter((station) => !placedDeviceIds.has(station.deviceId));

  return (
    <Box data-testid="scribe-presence-section">
      {/* #3630 -- a small section title sitting right on its table. */}
      <Typography variant="subtitle1" component="h2" color="text.primary" sx={{ mb: 0.5, fontWeight: 500 }}>
        {t("scribePresence.title")}
      </Typography>

        {/* #3090 -- the station status strip belongs with the scribes, and
            renders nothing when none are connected AND nothing is pending (#3097). */}
        <StationMonitorStripView showId={showId} rows={stripRows} loading={monitor.loading} error={monitor.error} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="scribe-presence-load-error">
            {t("scribePresence.loadError")}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">{t("scribePresence.noSeats")}</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small" data-testid="scribe-presence-table">
              <TableHead>
                <TableRow>
                  <TableCell>{t("scribePresence.columns.ring")}</TableCell>
                  <TableCell>{t("scribePresence.columns.seat")}</TableCell>
                  <TableCell>{t("scribePresence.columns.scribe")}</TableCell>
                  <TableCell>{t("scribePresence.columns.classCno")}</TableCell>
                  <TableCell>{t("scribePresence.columns.lastSeen")}</TableCell>
                  <TableCell sx={STATUS_STICKY}>{t("scribePresence.columns.status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={presenceKey(row)}
                    data-testid={`scribe-presence-row-${row.ring}-${row.seat}`}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {row.ring}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {row.seat}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.scribeName ? (
                        <Typography variant="body2" color="text.primary">
                          {row.scribeName}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          data-testid={`scribe-presence-not-connected-${row.ring}-${row.seat}`}
                        >
                          {t("scribePresence.notConnected")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell data-testid={`scribe-presence-work-${row.ring}-${row.seat}`}>
                      {(() => {
                        const { classLabel, cno } = presenceWork(row, { classesById, ridesById });
                        return (
                          <>
                            {classLabel && (
                              <Typography variant="body2" color="text.primary">
                                {classLabel}
                              </Typography>
                            )}
                            {cno && (
                              <Typography variant="body2" color="text.secondary">
                                {t("scribePresence.cno", { cno })}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {!row.everConnected ? (
                        <Typography variant="body2" color="text.secondary">
                          {"—"}
                        </Typography>
                      ) : row.quiet ? (
                        <Alert
                          severity="error"
                          icon={false}
                          sx={{ py: 0, px: 1, display: "inline-flex" }}
                          data-testid={`scribe-presence-quiet-${row.ring}-${row.seat}`}
                        >
                          {t("scribePresence.lastSeen", { time: formatElapsedLabel(row.elapsedMs) })}
                        </Alert>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.primary"
                          data-testid={`scribe-presence-lastseen-${row.ring}-${row.seat}`}
                        >
                          {t("scribePresence.lastSeen", { time: formatElapsedLabel(row.elapsedMs) })}
                        </Typography>
                      )}
                    </TableCell>
                    <StationRowStatus
                      exception={rowExceptions.get(presenceKey(row))?.exception ?? null}
                      testId={`scribe-presence-status-${row.ring}-${row.seat}`}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
    </Box>
  );
}
